import { shallowRef, type ShallowRef } from 'vue';
import { isNyxRead, requestSchema, type NyxRequest } from '@atum-remote/nyx-link-protocol';
import type { RemoteUiAdapter } from './model.js';
import type { NyxConnection } from './nyx-session.js';

// Keep transient request bodies within the authenticated adapter's lifetime,
// outside a device panel. Never serialize prompts into browser storage.
const adapters = new WeakMap<RemoteUiAdapter, Map<string, ShallowRef<NyxRequest | null>>>();
const initialMessages = new WeakMap<
  ShallowRef<NyxRequest | null>,
  ShallowRef<{ startId: string; text: string; taskId?: string } | null>
>();

/** Keep the unsent first message with its start receipt across view remounts. */
export function nyxInitialMessage(receipt: ShallowRef<NyxRequest | null>) {
  let message = initialMessages.get(receipt);
  if (!message) {
    message = shallowRef<{ startId: string; text: string; taskId?: string } | null>(null);
    initialMessages.set(receipt, message);
  }
  return message;
}

export async function sendNyxRequest(
  channel: NyxConnection,
  request: NyxRequest,
  receipt: ShallowRef<NyxRequest | null>,
  applyResult?: (result: unknown) => void | boolean,
) {
  // Local validation cannot create an uncertain outcome: nothing was transmitted.
  requestSchema.parse(request);
  if (!isNyxRead(request.operation)) receipt.value = request;
  // A rejection leaves the pre-registered receipt in place. An obsolete
  // transport callback must never reinstall it after another view reconciles it.
  const response = await channel.request(request);
  const retain =
    response.error === null &&
    receipt.value?.id === request.id &&
    applyResult?.(response.result) === false;
  if (
    !retain &&
    response.error !== 'ipc_request_outcome_unknown' &&
    receipt.value?.id === request.id
  )
    receipt.value = null;
  return response;
}

export function nyxRecovery(adapter: RemoteUiAdapter | undefined, deviceId: string) {
  if (!adapter) return shallowRef<NyxRequest | null>(null);
  let devices = adapters.get(adapter);
  if (!devices) {
    devices = new Map();
    adapters.set(adapter, devices);
  }
  let receipt = devices.get(deviceId);
  if (!receipt) {
    receipt = shallowRef<NyxRequest | null>(null);
    devices.set(deviceId, receipt);
  }
  return receipt;
}

export function clearNyxRecovery(adapter: RemoteUiAdapter) {
  const devices = adapters.get(adapter);
  if (devices)
    for (const receipt of devices.values()) {
      receipt.value = null;
      const message = initialMessages.get(receipt);
      if (message) message.value = null;
      initialMessages.delete(receipt);
    }
  adapters.delete(adapter);
}
